import * as React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
    ArrowDownRight,
    ArrowLeft,
    ArrowUpRight,
    Copy,
    ExternalLink,
    Megaphone,
    Pencil,
    Trash2,
} from "lucide-react";
import { EmptyState } from "@/components/ui/empty";
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Spinner } from "@/components/ui/spinner";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/page-header";
import { AffiliateOwnerBadge } from "@/components/reassign-affiliate-popover";
import { useConfirm } from "@/components/ui/confirm-dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    deleteCampaign,
    getCampaign,
    getCampaignStats,
    updateCampaign,
} from "@/api/campaigns";
import type { CampaignAnalyticsDto, NamedCount } from "@/types/campaign";
import { apiErrorMessage } from "@/lib/api";
import { cn, formatRelativeTime } from "@/lib/utils";
import { PixelsCard } from "@/components/pixels-card";
import { CampaignCommercialsCard } from "@/components/campaign-commercials-card";

export function CampaignDetailPage(): React.ReactNode {
    const { id } = useParams<{ id: string }>();
    const campaignId = Number(id);
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const confirmHook = useConfirm();
    const [range, setRange] = React.useState<"7d" | "30d" | "90d">("30d");

    const campaignQuery = useQuery({
        queryKey: ["campaign", campaignId],
        queryFn: () => getCampaign(campaignId),
        enabled: !Number.isNaN(campaignId),
    });
    const statsQuery = useQuery({
        queryKey: ["campaign", campaignId, "stats", range],
        queryFn: () => getCampaignStats(campaignId, range),
        enabled: !Number.isNaN(campaignId),
        refetchInterval: 60_000,
    });

    const updateMut = useMutation({
        mutationFn: (req: Parameters<typeof updateCampaign>[1]) => updateCampaign(campaignId, req),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["campaign", campaignId] });
            queryClient.invalidateQueries({ queryKey: ["campaigns"] });
            toast.success("Saved");
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Save failed")),
    });

    const deleteMut = useMutation({
        mutationFn: () => deleteCampaign(campaignId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["campaigns"] });
            toast.success("Campaign deleted");
            navigate("/campaigns");
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Delete failed")),
    });

    const c = campaignQuery.data;
    const s = statsQuery.data;
    const [editName, setEditName] = React.useState("");
    const [editPath, setEditPath] = React.useState("");
    const [editNotes, setEditNotes] = React.useState("");
    React.useEffect(() => {
        if (c) {
            setEditName(c.name);
            setEditPath(c.landingPath);
            setEditNotes(c.notes ?? "");
        }
    }, [c]);

    if (campaignQuery.isLoading) {
        return <div className="flex min-h-[50vh] items-center justify-center"><Spinner size="lg" /></div>;
    }
    if (!c) {
        return (
            <>
                <PageHeader title={`Campaign #${campaignId}`} breadcrumbs={[{ label: "Campaigns", href: "/campaigns" }, { label: `#${campaignId}` }]} />
                <div className="p-6">
                    <EmptyState
                        icon={<Megaphone className="h-8 w-8" />}
                        title="Campaign not found"
                        description="It may have been deleted, or the id is wrong."
                        action={
                            <Button asChild>
                                <Link to="/campaigns"><ArrowLeft className="h-4 w-4" /> Back to campaigns</Link>
                            </Button>
                        }
                    />
                </div>
            </>
        );
    }

    const dirty = c.name !== editName || c.landingPath !== editPath || (c.notes ?? "") !== editNotes;
    const copyLink = (link: string | null) => {
        if (!link) return;
        navigator.clipboard.writeText(link).then(
            () => toast.success("Link copied"),
            () => toast.error("Couldn't copy"),
        );
    };

    return (
        <>
            <PageHeader
                title={c.name}
                breadcrumbs={[{ label: "Campaigns", href: "/campaigns" }, { label: c.name }]}
                description={
                    <span className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className="font-mono">{c.slug}</Badge>
                        <Link to={`/domains/${c.domainId}`} className="hover:underline">{c.domainHostname}</Link>
                        <StatusBadge status={c.status} />
                        <AffiliateOwnerBadge
                            entityType="campaign"
                            entityId={c.id}
                            currentAffiliateId={c.affiliateId ?? null}
                            data-testid="campaign-affiliate-badge"
                        />
                        <span>· created {formatRelativeTime(c.createdAt)}</span>
                    </span>
                }
                actions={
                    <div className="flex items-center gap-2">
                        {/* Quick status toggle in the header — was buried at the bottom of the Edit card. */}
                        <Select
                            value={c.status}
                            onValueChange={(v) => updateMut.mutate({ status: v as "active" | "paused" | "archived" })}
                        >
                            <SelectTrigger className="h-9 w-[120px]" data-testid="campaign-header-status">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="active">Active</SelectItem>
                                <SelectItem value="paused">Paused</SelectItem>
                                <SelectItem value="archived">Archived</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={range} onValueChange={(v) => setRange(v as typeof range)}>
                            <SelectTrigger className="h-9 w-[130px]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="7d">Last 7 days</SelectItem>
                                <SelectItem value="30d">Last 30 days</SelectItem>
                                <SelectItem value="90d">Last 90 days</SelectItem>
                            </SelectContent>
                        </Select>
                        <Button variant="outline" size="sm" onClick={() => confirmHook.show({
                            title: `Delete "${c.name}"?`,
                            description: "Past clicks and leads stay attributed to this campaign id (we don't rewrite historical rows). You just won't be able to send new traffic here.",
                            destructive: true,
                            confirmLabel: "Delete campaign",
                            onConfirm: async () => {
                                await new Promise<void>((resolve, reject) => {
                                    deleteMut.mutate(undefined, { onSuccess: () => resolve(), onError: (e) => reject(e) });
                                });
                            },
                        })}>
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete
                        </Button>
                    </div>
                }
            />
            <div className="space-y-6 px-6 py-4">

            {/* Affiliate link */}
            <div className="rounded-md border border-border bg-card p-4">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Affiliate link</div>
                <div className="mt-2 flex items-center gap-2">
                    <code className="flex-1 truncate rounded bg-muted/40 px-2 py-1.5 font-mono text-sm">{c.link}</code>
                    <Button type="button" size="sm" variant="outline" onClick={() => copyLink(c.link)} data-testid="detail-copy-link">
                        <Copy className="h-3.5 w-3.5" /> Copy
                    </Button>
                    <a href={c.link ?? "#"} target="_blank" rel="noopener noreferrer">
                        <Button type="button" size="sm" variant="outline">
                            <ExternalLink className="h-3.5 w-3.5" /> Open
                        </Button>
                    </a>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                    Send this exact URL to the affiliate network. Visitors land on{" "}
                    <code>{c.landingPath}</code>; their click + lead get attributed to this campaign automatically.
                </p>
            </div>

            {/* Pixels attached to this campaign */}
            <PixelsCard campaignId={c.id} />

            {/* Resolved commercial terms per action type (advertiser vs affiliate side) */}
            <CampaignCommercialsCard campaignId={c.id} />

            {/* KPIs */}
            {s ? <KpiRow s={s} /> : <div className="flex justify-center py-8"><Spinner /></div>}

            {/* Daily series */}
            {s && s.daily.length > 0 && (
                <div className="rounded-md border border-border bg-card p-4">
                    <div className="mb-2 text-sm font-medium">Daily clicks · visitors · leads</div>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={s.daily} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="cclicks" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="hsl(217 91% 60%)" stopOpacity={0.5} />
                                        <stop offset="100%" stopColor="hsl(217 91% 60%)" stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="cleads" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="hsl(142 71% 45%)" stopOpacity={0.5} />
                                        <stop offset="100%" stopColor="hsl(142 71% 45%)" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                                <Tooltip />
                                <Legend />
                                <Area dataKey="clicks"   name="Clicks"   stroke="hsl(217 91% 60%)" strokeWidth={2} fill="url(#cclicks)" />
                                <Area dataKey="visitors" name="Visitors" stroke="hsl(280 65% 60%)" strokeWidth={1.5} fillOpacity={0} />
                                <Area dataKey="leads"    name="Leads"    stroke="hsl(142 71% 45%)" strokeWidth={2} fill="url(#cleads)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* Breakdowns */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <BreakdownCard title="By ad source" data={s?.bySource ?? []} />
                <BreakdownCard title="By country"   data={s?.byCountry ?? []} />
                <BreakdownCard title="By referrer"  data={s?.byReferrer ?? []} />
            </div>

            {/* Top landings + recent leads */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="rounded-md border border-border bg-card p-4">
                    <div className="mb-2 text-sm font-medium">Top landing paths</div>
                    {s && s.topLanding.length > 0 ? (
                        <table className="w-full text-xs">
                            <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                <tr><th className="px-2 py-1 text-left">Path</th>
                                    <th className="px-2 py-1 text-right">Clicks</th>
                                    <th className="px-2 py-1 text-right">Leads</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border/50">
                                {s.topLanding.map((p) => (
                                    <tr key={p.path}>
                                        <td className="px-2 py-1 font-mono">{p.path}</td>
                                        <td className="px-2 py-1 text-right tabular-nums">{p.clicks}</td>
                                        <td className="px-2 py-1 text-right tabular-nums">{p.leads}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : <div className="text-xs text-muted-foreground">No landing data yet.</div>}
                </div>
                <div className="rounded-md border border-border bg-card p-4">
                    <div className="mb-2 flex items-center justify-between text-sm font-medium">
                        <span>Recent leads</span>
                        {s && s.recentLeads.length > 0 && (
                            <Link to={`/leads?campaignId=${campaignId}`} className="text-xs text-muted-foreground hover:underline">View all →</Link>
                        )}
                    </div>
                    {s && s.recentLeads.length > 0 ? (
                        <ul className="space-y-1.5 text-xs">
                            {s.recentLeads.map((l) => (
                                <li key={l.id}>
                                    <Link
                                        to={`/leads?id=${l.id}`}
                                        className="flex items-center justify-between rounded px-1 py-0.5 hover:bg-accent/50"
                                    >
                                        <span className="truncate font-mono">{l.email ?? `#${l.id}`}</span>
                                        <span className="text-muted-foreground">
                                            {l.primarySource ? `${l.primarySource} · ` : ""}{formatRelativeTime(l.createdAt)}
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    ) : <div className="text-xs text-muted-foreground">No leads yet.</div>}
                </div>
            </div>

            {/* Edit section */}
            <div className="rounded-md border border-border bg-card p-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-medium">
                    <Pencil className="h-4 w-4" /> Edit
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div className="space-y-1.5">
                        <Label htmlFor="cname">Name</Label>
                        <Input id="cname" value={editName} onChange={(e) => setEditName(e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="cpath">Landing path</Label>
                        <Input id="cpath" value={editPath} onChange={(e) => setEditPath(e.target.value)} />
                    </div>
                </div>
                <div className="mt-3 space-y-1.5">
                    <Label htmlFor="cnotes">Notes</Label>
                    <Textarea id="cnotes" rows={3} value={editNotes} onChange={(e) => setEditNotes(e.target.value)} />
                </div>
                <div className="mt-3 flex justify-end gap-2">
                    <Select value={c.status} onValueChange={(v) => updateMut.mutate({ status: v as "active" | "paused" | "archived" })}>
                        <SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="paused">Paused</SelectItem>
                            <SelectItem value="archived">Archived</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button
                        variant="default"
                        size="sm"
                        disabled={!dirty || updateMut.isPending}
                        onClick={() => updateMut.mutate({
                            name: editName !== c.name ? editName : undefined,
                            landingPath: editPath !== c.landingPath ? editPath : undefined,
                            notes: editNotes !== (c.notes ?? "") ? editNotes : undefined,
                        })}
                    >
                        Save changes
                    </Button>
                </div>
            </div>
        </div>
        {confirmHook.dialog}
        </>
    );
}

function KpiRow({ s }: { s: CampaignAnalyticsDto }): React.ReactNode {
    const cells: { label: string; value: string | number; prev: number; pct?: boolean }[] = [
        { label: "Clicks",         value: s.clicks,                       prev: s.previous.clicks },
        { label: "Unique visitors",value: s.uniqueVisitors,               prev: s.previous.uniqueVisitors },
        { label: "Leads",          value: s.leads,                        prev: s.previous.leads },
        { label: "Conversion rate",value: `${(s.conversionRate*100).toFixed(2)}%`, prev: s.previous.conversionRate*100, pct: true },
    ];
    return (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {cells.map((c) => {
                const prev = c.prev;
                const cur = typeof c.value === "string"
                    ? parseFloat(String(c.value).replace("%",""))
                    : c.value;
                const delta = prev === 0 ? 0 : ((cur as number) - prev) / Math.abs(prev) * 100;
                const up = delta >= 0;
                return (
                    <div key={c.label} className="rounded-md border border-border bg-card p-3">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.label}</div>
                        <div className="mt-1 text-2xl font-semibold tabular-nums">{c.value}</div>
                        {prev > 0 && (
                            <div className={cn(
                                "mt-1 inline-flex items-center gap-0.5 text-[11px] tabular-nums",
                                up ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400",
                            )}>
                                {up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                                {delta.toFixed(1)}% vs prior period
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function BreakdownCard({ title, data }: { title: string; data: NamedCount[] }): React.ReactNode {
    return (
        <div className="rounded-md border border-border bg-card p-4">
            <div className="mb-2 text-sm font-medium">{title}</div>
            {data.length === 0 ? (
                <div className="text-xs text-muted-foreground">No data.</div>
            ) : data.length < 3 ? (
                // Sparse data renders as a single bar chart axis with awkward whitespace;
                // a small list is clearer at this density.
                <ul className="space-y-1.5 text-xs">
                    {data.map((d) => (
                        <li key={d.name} className="flex items-center justify-between gap-2">
                            <span className="truncate">{d.name || "—"}</span>
                            <span className="font-mono text-muted-foreground tabular-nums">{d.count}</span>
                        </li>
                    ))}
                </ul>
            ) : (
                <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.slice(0, 8)} layout="vertical" margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                            <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                            <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={90} />
                            <Tooltip />
                            <Bar dataKey="count" fill="hsl(217 91% 60%)" radius={[0, 4, 4, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
}

function StatusBadge({ status }: { status: string }): React.ReactNode {
    const cls =
        status === "active" ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300" :
        status === "paused" ? "bg-amber-100 text-amber-900 dark:bg-amber-950/40 dark:text-amber-300" :
        "bg-muted text-muted-foreground";
    return <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-medium uppercase", cls)}>{status}</span>;
}
